import { BASE_URL } from '@constants/api'
import {
	LegalDocumentDetail,
	LegalDocumentCreateRequest,
	LegalDocumentListRequest,
	LegalDocumentListResponse,
	LegalDocumentReport,
	LegalProvisionDetail,
	LegalProvisionListRequest,
	LegalProvisionListResponse,
} from '@models/legalDocument.model'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { setAuthorization } from '@utils/helpers'

// O acervo de documentos.
//
// Sem cópia no MySQL, ao contrário das conversões: o acervo é o microserviço, e
// um espelho daria duas verdades sobre a mesma norma sem forma de saber qual
// está certa. O que se lê aqui é o que ele responde agora.

export const legalDocumentApi = createApi({
	reducerPath: 'legalDocumentApi',
	tagTypes: ['LegalDocuments', 'LegalDocument', 'LegalProvisions'],
	baseQuery: fetchBaseQuery({
		baseUrl: BASE_URL,
		prepareHeaders: setAuthorization,
	}),
	endpoints: (builder) => ({
		list: builder.query<LegalDocumentListResponse, LegalDocumentListRequest>({
			query: ({ page = 1, limit = 20, search }) => ({
				url: `/ai/legal-documents?page=${page}&limit=${limit}${
					search ? `&search=${encodeURIComponent(search)}` : ''
				}`,
				method: 'GET',
			}),
			providesTags: ['LegalDocuments'],
		}),

		show: builder.query<LegalDocumentDetail, { id: string }>({
			query: ({ id }) => ({
				url: `/ai/legal-documents/${encodeURIComponent(id)}`,
				method: 'GET',
			}),
			providesTags: ['LegalDocument'],
		}),

		// A resposta é o relatório da carga, e não a norma: o envio processa o
		// documento inteiro antes de voltar, então o que interessa a quem subiu é
		// o que se entendeu dele — quantos dispositivos entraram, o que ficou de
		// aviso ou de pendência.
		create: builder.mutation<LegalDocumentReport, LegalDocumentCreateRequest>({
			query: (data) => {
				const formData = new FormData()

				formData.append('file', data.file[0])

				if (data.title) {
					formData.append('title', data.title)
				}

				if (data.sourceUrl) {
					formData.append('sourceUrl', data.sourceUrl)
				}

				data.scopes?.forEach((scope) => {
					formData.append('scopes[]', scope)
				})

				data.nicknames?.forEach((nickname) => {
					formData.append('nicknames[]', nickname)
				})

				return {
					url: `/ai/legal-documents`,
					method: 'POST',
					body: formData,
				}
			},
			invalidatesTags: ['LegalDocuments'],
		}),

		// Corrige o título e, com ele, o endereço da norma. Existe porque o
		// título pode ter sido inferido: a citação impressa acerta na maioria e
		// erra em alguma, e sem conserto o caminho seria apagar e subir de novo.
		//
		// `sourceUrl` entra pela mesma razão: quem subiu o documento sem dizer de
		// onde ele veio ficava com o marcador `admin://arquivo` gravado para
		// sempre. Campo ausente preserva o que está lá — o acervo não tem lista
		// vazia que apague procedência, porque captura sem endereço deixa de
		// provar origem.
		retitle: builder.mutation<
			LegalDocumentDetail,
			{
				id: string
				title: string
				scopes?: string[]
				nicknames?: string[]
				sourceUrl?: string
			}
		>({
			query: ({ id, title, scopes, nicknames, sourceUrl }) => ({
				url: `/ai/legal-documents/${encodeURIComponent(id)}`,
				method: 'PATCH',
				body: {
					title,
					...(scopes ? { scopes } : {}),
					...(nicknames ? { nicknames } : {}),
					...(sourceUrl ? { sourceUrl } : {}),
				},
			}),
			invalidatesTags: ['LegalDocuments', 'LegalDocument'],
		}),

		// O desfazer de um documento subido errado — título trocado, arquivo de
		// outra norma. A tela que deixa subir tem de deixar retirar.
		remove: builder.mutation<{ message: string }, { id: string }>({
			query: ({ id }) => ({
				url: `/ai/legal-documents/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['LegalDocuments', 'LegalDocument'],
		}),

		// Os dispositivos da norma, que é o que a tela não tinha: até aqui ela
		// mostrava a contagem e mais nada, e não havia caminho para abrir um.
		provisions: builder.query<
			LegalProvisionListResponse,
			LegalProvisionListRequest
		>({
			query: ({ id, page = 1, limit = 50, search, status }) => ({
				url: `/ai/legal-documents/${encodeURIComponent(
					id
				)}/provisions?page=${page}&limit=${limit}${
					search ? `&search=${encodeURIComponent(search)}` : ''
				}${status ? `&status=${encodeURIComponent(status)}` : ''}`,
				method: 'GET',
			}),
			providesTags: ['LegalProvisions'],
		}),

		// Um dispositivo com todas as redações que ele já teve.
		//
		// O caminho tem barras — "art-5/inc-78/ali-a" — e elas seguem cruas: é um
		// endereço só, e escapá-lo inteiro entregaria %2F no meio de algo que não
		// existe em norma nenhuma.
		provision: builder.query<
			LegalProvisionDetail,
			{ id: string; path: string }
		>({
			query: ({ id, path }) => ({
				url: `/ai/legal-documents/${encodeURIComponent(id)}/provisions/${path
					.split('/')
					.map((segment) => encodeURIComponent(segment))
					.join('/')}`,
				method: 'GET',
			}),
			providesTags: ['LegalProvisions'],
		}),

		// A situação que a curadoria escreve no dispositivo derrubado por um
		// tribunal. Revogação não passa por aqui: ela vem da página da norma.
		markProvision: builder.mutation<
			{ endereco: string; situacao: string },
			{ id: string; path: string; status: string; annotation?: string | null }
		>({
			query: ({ id, path, status, annotation }) => ({
				url: `/ai/legal-documents/${encodeURIComponent(id)}/provisions`,
				method: 'PATCH',
				body: {
					path,
					status,
					...(annotation === undefined ? {} : { annotation }),
				},
			}),
			invalidatesTags: ['LegalProvisions', 'LegalDocument'],
		}),
	}),
})
